// Why a contact or its company left the pipeline: the vocabulary shared by the
// verdict dialog and the exit-reason pie.
//
// DISQUALIFY_T is spread into TASK_BOARD_T (like STEP_LABEL_T), so a motive is
// named the same wherever it appears and lib never imports an app-dir map.
import {
  CalendarClock,
  Linkedin,
  type LucideIcon,
  Mail,
  Phone,
} from "lucide-react"

import type {
  DisqualifyOutcome,
  DisqualifyReason,
  DisqualifyScope,
  LostReason,
  ResumeChannel,
  TaskExitReasonCount,
} from "@/nango/types/apps.autoleads/autoleads/TaskBoard"

export const DISQUALIFY_T = {
  reasonNotInterested: { FR: "Pas intéressé", EN: "Not interested" },
  reasonOutOfTarget: { FR: "Hors cible", EN: "Out of target" },
  reasonWrongNumber: { FR: "Mauvais numéro", EN: "Wrong number" },
  reasonNoAnswer: { FR: "Sans réponse", EN: "No answer" },
  reasonUnreachable: { FR: "Injoignable", EN: "Unreachable" },
  reasonCompetitor: { FR: "Déjà équipé", EN: "Already equipped" },
  reasonCompanyClosed: { FR: "Entreprise fermée", EN: "Company closed" },
  reasonBadTiming: { FR: "Mauvais timing", EN: "Bad timing" },
  reasonOther: { FR: "Autre", EN: "Other" },
  lostNoBudget: { FR: "Pas de budget", EN: "No budget" },
  lostCompetitor: {
    FR: "Parti chez un concurrent",
    EN: "Went to a competitor",
  },
  lostGhosted: { FR: "Ne répond plus", EN: "Stopped answering" },
  lostRefused: { FR: "Offre refusée", EN: "Turned the offer down" },
  lostOutOfScope: { FR: "Finalement hors cible", EN: "Not a fit after all" },
  lostOther: { FR: "Autre", EN: "Other" },
  reasonNaturalExit: { FR: "Sorti du pipeline", EN: "Left the pipeline" },
  reasonUnspecified: { FR: "Non précisé", EN: "Unspecified" },
  reasonTrackStopped: {
    FR: "Désabonné ou rejeté",
    EN: "Unsubscribed or bounced",
  },
  resumeByPhone: { FR: "Par téléphone", EN: "By phone" },
  resumeByEmail: { FR: "Par email", EN: "By email" },
  resumeByLinkedin: { FR: "Sur LinkedIn", EN: "On LinkedIn" },
}

type ReasonLabelKey = keyof typeof DISQUALIFY_T

// Display order of the dialog's radio list: the two most common verdicts first,
// then the rest, with the free-text escape hatch last.
export const DISQUALIFY_REASONS: readonly DisqualifyReason[] = [
  "not_interested",
  "out_of_target",
  "wrong_number",
  "no_answer",
  "unreachable",
  "competitor",
  "company_closed",
  "bad_timing",
  "other",
]

// What a queued colleague can be given. Postponing is a decision about the
// COMPANY, taken on the contact a rep actually spoke to, so it is not on offer
// from a card of somebody nobody has called yet.
export const DISQUALIFY_REASONS_QUEUED: readonly DisqualifyReason[] =
  DISQUALIFY_REASONS.filter((reason) => reason !== "bad_timing")

// Motives that describe a PERSON, so they cannot write off a company. Mirrors
// the backend's OUTREACH_DISQUALIFY_REASONS_CONTACT_ONLY. Bad timing is NOT one
// of them: it ends the whole company's cycle whatever scope was asked.
const CONTACT_ONLY_REASONS: readonly DisqualifyReason[] = [
  "wrong_number",
  "no_answer",
  "unreachable",
]

// Display order of the lost dialog's radio list: the two answers a rep gives
// most often first, the free-text escape hatch last.
export const LOST_REASONS: readonly LostReason[] = [
  "no_budget",
  "lost_competitor",
  "ghosted",
  "refused",
  "out_of_scope",
  "lost_other",
]

export const DISQUALIFY_REASON_LABEL: Record<
  DisqualifyOutcome,
  ReasonLabelKey
> = {
  not_interested: "reasonNotInterested",
  out_of_target: "reasonOutOfTarget",
  wrong_number: "reasonWrongNumber",
  no_answer: "reasonNoAnswer",
  unreachable: "reasonUnreachable",
  competitor: "reasonCompetitor",
  company_closed: "reasonCompanyClosed",
  bad_timing: "reasonBadTiming",
  other: "reasonOther",
  no_budget: "lostNoBudget",
  lost_competitor: "lostCompetitor",
  ghosted: "lostGhosted",
  refused: "lostRefused",
  out_of_scope: "lostOutOfScope",
  lost_other: "lostOther",
  natural_exit: "reasonNaturalExit",
  unspecified: "reasonUnspecified",
}

// One token per motive, so a slice keeps its colour between the arc and the
// legend, and between two renders. Tokens only: --chart-1..10 are defined in
// globals.css for exactly this.
export const DISQUALIFY_COLOR: Record<DisqualifyOutcome, string> = {
  not_interested: "var(--chart-1)",
  out_of_target: "var(--chart-2)",
  wrong_number: "var(--chart-3)",
  no_answer: "var(--chart-4)",
  unreachable: "var(--chart-5)",
  competitor: "var(--chart-6)",
  company_closed: "var(--chart-7)",
  bad_timing: "var(--chart-8)",
  other: "var(--chart-9)",
  unspecified: "var(--chart-10)",
  no_budget: "var(--chart-11)",
  lost_competitor: "var(--chart-12)",
  ghosted: "var(--chart-13)",
  refused: "var(--chart-14)",
  out_of_scope: "var(--chart-15)",
  lost_other: "var(--chart-16)",
  // The bucket nobody chose reads as neutral, not as a verdict.
  natural_exit: "var(--muted-foreground)",
}

export type DisqualifyVerdict = {
  scope: DisqualifyScope
  reason: DisqualifyReason | ""
  note: string
  // Both carried by "bad_timing" only.
  resume_at: string | null
  resume_channel: ResumeChannel | ""
}

// How the rep means to reach a postponed company, in the order a B2B rep
// actually reaches for them.
export const RESUME_CHANNELS: readonly ResumeChannel[] = [
  "phone",
  "email",
  "linkedin",
]

export const RESUME_CHANNEL_LABEL: Record<ResumeChannel, ReasonLabelKey> = {
  phone: "resumeByPhone",
  email: "resumeByEmail",
  linkedin: "resumeByLinkedin",
}

// The same three marks the sequence steps carry (see STEP_META), so a channel
// looks the same whether it is being chosen or being worked.
// The mark of a task the cycle filed rather than a rep: a postponed company's
// call back. Named apart from the channel, which a rep may leave unset.
export const POSTPONED_TASK_ICON: LucideIcon = CalendarClock

export const RESUME_CHANNEL_ICON: Record<ResumeChannel, LucideIcon> = {
  phone: Phone,
  email: Mail,
  linkedin: Linkedin,
}

// Whether a company can be written off for this motive.
// A stored motive the labels know: older rows and automatic stops carry others.
export function isDisqualifyReason(value: string): value is DisqualifyOutcome {
  return value in DISQUALIFY_REASON_LABEL
}

export function isCompanyReason(reason: DisqualifyReason | ""): boolean {
  return reason !== "" && !CONTACT_ONLY_REASONS.includes(reason)
}

// Whether the dialog's confirm button may be pressed. Mirrors the endpoint's
// own refusals, so a rep never sends a verdict the server will reject.
export function isDisqualifyReady(
  draft: DisqualifyVerdict,
  now: Date = new Date(),
): boolean {
  if (!draft.reason) return false
  if (draft.scope === "lead" && !isCompanyReason(draft.reason)) return false
  if (draft.reason === "other" && !draft.note.trim()) return false
  if (draft.reason === "bad_timing") {
    return draft.resume_at !== null && new Date(draft.resume_at) > now
  }
  return true
}

// The pie's slices: nothing empty, biggest verdict first, and the bucket nobody
// chose pinned last so a natural exit never reads as a decision.
export function exitSlices(rows: TaskExitReasonCount[]): TaskExitReasonCount[] {
  const drawn = rows.filter((row) => row.contacts > 0)
  const verdicts = drawn
    .filter((row) => row.reason !== "natural_exit")
    .sort((a, b) => b.contacts - a.contacts)
  return [...verdicts, ...drawn.filter((row) => row.reason === "natural_exit")]
}

// Whether the lost dialog's confirm button may be pressed. Mirrors the
// endpoint's refusals, so a rep never sends a verdict the server will reject.
export function isLostReady(reason: LostReason | "", note: string): boolean {
  if (!reason) return false
  return reason !== "lost_other" || Boolean(note.trim())
}
